import { SearchOutlined } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Card, IconButton, InputAdornment, TextField, Tooltip, styled } from "@mui/material"
import { forwardRef, useContext, useLayoutEffect, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"

import useResponsive from "@sap/hooks/useResponsive"
import { pxToRem } from "@sap/theme/typography"

import ImageSearchButton from "@/pages/image-search/ImageSearchButton"
import { sendSearchEvent } from "@/utils/posthog"

import { MainLayoutContext } from "."

export const SearchOutlinedIcon = styled(SearchOutlined)(({ theme }) => ({
  color: theme.palette.grey[600],
}))

export const SearchResultCard = styled(Card)({
  zIndex: 99,
  top: "100%",
  width: "100%",
  position: "absolute",
  paddingTop: "0.5rem",
  paddingBottom: "0.5rem",
})

const SearchInput = forwardRef(function SearchInput(props, ref) {
  const parentRef = useRef()
  const { t } = useTranslation("common")
  const { showMobileSearch, setShowMobileSearch } = useContext(MainLayoutContext)
  const upMd = useResponsive("up", "md")
  const navigate = useNavigate()
  const [search, setSearch] = useState("")

  const location = useLocation()

  useLayoutEffect(() => {
    if (location.pathname !== "/") {
      setSearch("")
    }
  }, [location.pathname])

  useLayoutEffect(() => {
    if (location.pathname === "/") {
      const searchParams = new URLSearchParams(location.search)
      setSearch(searchParams.get("query") || "")
    }
  }, [location.pathname, location.search])

  const handleSearch = () => {
    setShowMobileSearch(false)
    sendSearchEvent(search)
    navigate({
      pathname: "/",
      ...(search && { search: `?query=${encodeURIComponent(search.trim())}` }),
    })
    window.scrollTo({ top: 0 })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      ;(e.target as HTMLInputElement).blur()
      handleSearch()
    }
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        position: "relative",
        flex: "1 1 0",
        minWidth: 0,
        maxWidth: "670px",
        mx: "auto",
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder={t("Searchingfor") + "..."}
        onChange={(e) => setSearch(e.target.value)}
        value={search}
        inputRef={ref}
        slotProps={{
          htmlInput: {
            "aria-label": t("Search"),
            onKeyDown: handleKeyDown,
          },
          input: {
            autoComplete: "off",
            spellCheck: "false",
            sx: {
              height: 48,
              px: 0.75,
              fontSize: pxToRem(16),
              borderRadius: 300,
              color: "grey.700",
              overflow: "hidden",
              backgroundColor: "background.paper",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.300",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.400",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& .MuiOutlinedInput-input": {
                minWidth: 0,
                px: 1,
              },
              "& .MuiIconButton-root": {
                width: 36,
                height: 36,
                flexShrink: 0,
                color: "grey.700",
              },
              "& .MuiIconButton-root.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: -3,
              },
              "& .MuiSvgIcon-root": {
                fontSize: pxToRem(20),
              },
            },
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0 }}>
                {search && (
                  <Tooltip title={t("ClearSearch")}>
                    <IconButton
                      aria-label={t("ClearSearch")}
                      onClick={() => {
                        setSearch("")
                        if (location.pathname === "/") {
                          navigate("/")
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <ImageSearchButton
                  variant="pill"
                  announcement={upMd || showMobileSearch}
                  onClick={() => setShowMobileSearch(false)}
                />
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <Tooltip title={t("Search")}>
                  <IconButton aria-label={t("Search")} onClick={handleSearch}>
                    <SearchOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  )
})
export default SearchInput
